/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { cnpjMask, cpfMask, documentKind } from '@/shared/utils/input-masks';

/** The two taxpayer ids an agency record can carry. */
export interface AgencyDocument {
  cnpj?: string | null;
  cpf?: string | null;
}

/**
 * The document the profile should show.
 *
 * Checkout stores a CPF on `cpf` and a CNPJ on `cnpj`. Reading only `cnpj`
 * hid every solo broker who signed up with a CPF.
 *
 * @param agency - The agency record.
 * @returns The stored document, CPF when that is all there is.
 */
export function storedDocument(agency: AgencyDocument): string {
  return (agency.cnpj || agency.cpf || '').trim();
}

/**
 * Which label the document field wears.
 *
 * @param agency - The agency record.
 * @returns The i18n key for CNPJ, CPF, or either.
 */
export function documentLabelKey(agency: AgencyDocument): string {
  if (agency.cnpj?.trim()) return 'v2.profile.cnpj';
  if (agency.cpf?.trim()) return 'v2.profile.cpf';
  return 'v2.profile.document';
}

/**
 * The name field's label.
 *
 * A solo broker has no razão social. What the profile edits is the office name.
 *
 * @param type - The agency type.
 * @returns The i18n key.
 */
export function registrationNameKey(type?: string | null): string {
  return type === 'solo_broker'
    ? 'agencyData.labels.nomeDoEscritorio'
    : 'agencyData.labels.razaoSocial';
}

/**
 * Formats whichever document the value is turning into.
 *
 * @param value - Raw or already masked.
 * @returns The masked value for display.
 */
export function maskDocument(value: string): string {
  if (!value) return '';
  const mask = documentKind(value) === 'cnpj' ? cnpjMask : cpfMask;
  return mask(value).masked;
}

/**
 * The patch a profile save should send for the document.
 *
 * A blank field is omitted. Sending `cnpj: ""` is what the API rejects as
 * "Invalid CNPJ format", which blocked renaming the office.
 *
 * @param raw - The form value, masked or not.
 * @returns `cnpj` or `cpf`, or nothing when the field was left empty.
 */
export function documentPatch(raw: string | undefined): { cnpj?: string; cpf?: string } {
  const value = (raw ?? '').trim();
  if (!value) return {};
  return documentKind(value) === 'cnpj' ? { cnpj: value } : { cpf: value };
}
